"use client";

import { useEffect, useCallback, useRef } from "react";
import { type Cart as CartData } from "@cappelendamm/e-commerce-services";

const CHANNEL_NAME = "cart-sync";

type CartBroadcastMessage = {
  type: "cart-update";
  cart: CartData;
  timestamp: number;
};

/**
 * Synchronizes cart state across browser tabs using BroadcastChannel API.
 * Falls back to storage events for environments without BroadcastChannel support.
 */
export function useCartBroadcast(onCartUpdate: (cart: CartData) => void) {
  const channelRef = useRef<BroadcastChannel | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const hasBroadcastChannel = typeof BroadcastChannel !== "undefined";

    if (hasBroadcastChannel) {
      const channel = new BroadcastChannel(CHANNEL_NAME);
      channelRef.current = channel;

      channel.onmessage = (event: MessageEvent<CartBroadcastMessage>) => {
        if (event.data?.type === "cart-update" && event.data.cart) {
          onCartUpdate(event.data.cart);
        }
      };

      return () => {
        channel.close();
        channelRef.current = null;
      };
    } else {
      // Fallback: use localStorage storage event for cross-tab sync
      const handleStorage = (event: StorageEvent) => {
        if (event.key === CHANNEL_NAME && event.newValue) {
          try {
            const message: CartBroadcastMessage = JSON.parse(event.newValue);
            if (message.type === "cart-update" && message.cart) {
              onCartUpdate(message.cart);
            }
          } catch {
            // Ignore parse errors
          }
        }
      };

      window.addEventListener("storage", handleStorage);
      return () => {
        window.removeEventListener("storage", handleStorage);
      };
    }
  }, [onCartUpdate]);

  const broadcast = useCallback((cart: CartData) => {
    const message: CartBroadcastMessage = {
      type: "cart-update",
      cart,
      timestamp: Date.now(),
    };

    if (channelRef.current) {
      channelRef.current.postMessage(message);
    } else if (
      typeof window !== "undefined" &&
      typeof BroadcastChannel === "undefined"
    ) {
      // Fallback: trigger storage event for other tabs
      localStorage.setItem(CHANNEL_NAME, JSON.stringify(message));
      // Remove immediately — we only need the event, not persistent storage
      localStorage.removeItem(CHANNEL_NAME);
    }
  }, []);

  return { broadcast };
}
